import { unwrap } from "@sagot/api-client";
import { PAYMENT_METHOD_TYPES, SELF_SERVE_METHOD_TYPES, type PaymentMethodKind } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, ErrorState, Field, Icon, Input, RadioGroup, Skeleton, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useApi } from "../../../session/session-provider";
import { gatewayConfig } from "./billing-model";
import { MethodMark } from "./method-mark";
import { useStartSetup } from "./method-setup";
import { TestModeNotice } from "./test-mode-notice";
import { digitsOnly, luhnValid, tokenizerFor, type PaymentTokenizer, type TokenizeInput } from "./tokenizer";
import { useInvalidateBilling } from "./use-billing";
import { useBillingConfig } from "../shared";

const EXPIRY = /^(0[1-9]|1[0-2])\s*\/\s*(\d{2})$/;
const MOBILE = /^(\+?63|0)9\d{9}$/;
const CENTURY = 2000;
/** Every kind the dialog knows. Which ones it OFFERS is the server's self-serve switch (DEC-155). */
const KINDS = ["paypal", ...PAYMENT_METHOD_TYPES] as const;
type Kind = (typeof KINDS)[number];

const schema = z
  .object({
    type: z.enum(KINDS),
    number: z.string(),
    expiry: z.string(),
    cvc: z.string(),
    mobile: z.string(),
    makeDefault: z.boolean(),
  })
  .superRefine((value, ctx) => {
    const fail = (path: string, message: string) => { ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message }); };
    if (value.type === "card") {
      if (!luhnValid(value.number)) fail("number", "billing.methods.errors.number");
      if (!EXPIRY.test(value.expiry.trim())) fail("expiry", "billing.methods.errors.expiry");
      if (!/^\d{3,4}$/.test(value.cvc.trim())) fail("cvc", "billing.methods.errors.cvc");
    }
    if ((value.type === "gcash" || value.type === "maya") && !MOBILE.test(digitsOnly(value.mobile).replace(/^63/, "0"))) fail("mobile", "billing.methods.errors.mobile");
  });
type FormValues = z.infer<typeof schema>;

function toTokenizeInput(values: FormValues, type: PaymentMethodKind): TokenizeInput {
  if (type === "card") {
    const [, month, year] = EXPIRY.exec(values.expiry.trim()) ?? [];
    return { type: "card", card: { number: values.number, expMonth: Number(month), expYear: CENTURY + Number(year), cvc: values.cvc.trim() } };
  }
  return { type, mobile: values.mobile };
}

/** In-app PayMongo method (card, GCash, Maya, bank): only offered while the server's switch allows it. */
function useAddTokenizedMethod(tokenizer: PaymentTokenizer, onDone: () => void) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (values: FormValues) => {
      if (values.type === "paypal") throw new Error("paypal_uses_hosted_setup");
      const token = await tokenizer.tokenize(toTokenizeInput(values, values.type));
      return unwrap(
        api.POST("/v1/billing/payment-methods", {
          body: { type: values.type, provider_method_id: token.providerMethodId, brand: token.brand, last4: token.last4, exp_month: token.expMonth, exp_year: token.expYear, make_default: values.makeDefault },
        }),
      );
    },
    onSuccess: async () => {
      toast.success(t("billing.methods.saved"));
      await invalidate();
      onDone();
    },
    onError: (caught) => toast.error(caught instanceof Error && caught.message === "tokenization_failed" ? t("billing.methods.errors.tokenize") : message(caught)),
  });
}

function TypeFields({ type, form }: { type: Kind; form: UseFormReturn<FormValues> }) {
  const { t } = useTranslation();
  const errors = form.formState.errors;
  const err = (key: keyof FormValues) => (errors[key]?.message ? t(errors[key].message) : undefined);
  if (type === "paypal") {
    return (
      <div className="space-y-2 rounded-md bg-surface-2 p-3 text-sm" data-testid="paypal-pane">
        <p>{t("billing.methods.paypalIntro")}</p>
        <p className="flex items-center gap-2 text-xs text-fg-muted">
          <Icon name="check-circle" className="size-4 shrink-0 text-success" />
          {t("billing.methods.paypalDefault")}
        </p>
      </div>
    );
  }
  if (type === "card") {
    return (
      <div className="space-y-3">
        <Field label={t("billing.methods.cardNumber")} error={err("number")}>
          <Input className="font-mono" inputMode="numeric" autoComplete="cc-number" placeholder="4242 4242 4242 4242" {...form.register("number")} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("billing.methods.expiry")} error={err("expiry")}>
            <Input autoComplete="cc-exp" placeholder="MM / YY" {...form.register("expiry")} />
          </Field>
          <Field label={t("billing.methods.cvc")} error={err("cvc")}>
            <Input autoComplete="cc-csc" inputMode="numeric" placeholder="123" {...form.register("cvc")} />
          </Field>
        </div>
      </div>
    );
  }
  if (type === "bank") {
    return (
      <div className="rounded-md bg-surface-2 p-3 text-sm">
        <div className="font-semibold">{t("billing.methods.bankTitle")}</div>
        <p className="mt-1 text-xs text-fg-muted">{t("billing.methods.bankBody")}</p>
      </div>
    );
  }
  return (
    <Field label={t(`billing.methods.mobile_${type}`)} hint={t(`billing.methods.mobileHint_${type}`)} error={err("mobile")}>
      <Input inputMode="tel" autoComplete="tel" placeholder="0917 123 4567" {...form.register("mobile")} />
    </Field>
  );
}

interface PaymentMethodDialogProps {
  onClose: () => void;
  /** "Other payment options: contact sales" opens the sales request form. */
  onContactSales: () => void;
}

/**
 * Add a payment method. Self-serve payment is PayPal only (DEC-155): the dialog offers the kinds the
 * server lists in `allowed_method_types` (PayPal), vaults the PayPal account through the hosted
 * setup and makes it the default for renewals and top-ups. The in-app card / GCash / Maya / bank
 * form stays in the code and appears again if the server's switch lists those kinds.
 */
export function PaymentMethodDialog({ onClose, onContactSales }: PaymentMethodDialogProps) {
  const { t } = useTranslation();
  const config = useBillingConfig();
  const allowed: readonly string[] = config.data?.allowed_method_types ?? SELF_SERVE_METHOD_TYPES;
  const options = KINDS.filter((kind) => allowed.includes(kind));
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { type: "paypal", number: "", expiry: "", cvc: "", mobile: "", makeDefault: true } });
  const tokenizer = tokenizerFor(gatewayConfig(config.data, "paymongo"));
  const add = useAddTokenizedMethod(tokenizer, onClose);
  const setup = useStartSetup(onClose);
  const [picked, makeDefault] = useWatch({ control: form.control, name: ["type", "makeDefault"] });
  const type: Kind = options.includes(picked) ? picked : (options[0] ?? "paypal");
  const loading = !config.data && !config.isError;
  const noneOffered = Boolean(config.data) && options.length === 0;
  const busy = type === "paypal" ? setup.isPending : add.isPending;
  const submit = form.handleSubmit((values) => {
    if (type === "paypal") setup.mutate("paypal");
    else add.mutate({ ...values, type });
  });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("billing.methods.addTitle")}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="pm-form" loading={busy} disabled={loading || noneOffered || (config.isError && type !== "paypal")}>
              {type === "paypal" ? t("billing.methods.paypalContinue") : t("billing.methods.save")}
            </Button>
          </>
        }
      >
        {config.isError && type !== "paypal" ? (
          <ErrorState headingLevel={3} title={t("billing.methods.errors.config")} onRetry={() => void config.refetch()} retryLabel={t("common.retry")} />
        ) : null}
        {loading ? (
          <div className="space-y-3" aria-hidden="true" data-testid="method-dialog-loading">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : noneOffered ? (
          <ErrorState headingLevel={3} title={t("billing.methods.paypalUnavailable")} />
        ) : (
          <form id="pm-form" className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
            <RadioGroup
              aria-label={t("billing.methods.typeLabel")}
              variant="cards"
              className={options.length > 1 ? "grid-cols-2" : "grid-cols-1"}
              value={type}
              onValueChange={(value) => { form.setValue("type", value); form.clearErrors(); }}
              options={options.map((value) => ({
                value,
                label: (
                  <span className="flex items-center gap-2.5">
                    <MethodMark type={value} small />
                    {t(value === "paypal" ? "billing.methods.paypalOption" : `billing.methods.type_${value}`)}
                  </span>
                ),
              }))}
            />
            <TypeFields type={type} form={form} />
            <TestModeNotice gateways={[type === "paypal" ? "paypal" : "paymongo"]} />
            {type === "paypal" ? null : <Checkbox label={t("billing.methods.makeDefault")} checked={makeDefault} onCheckedChange={(checked) => { form.setValue("makeDefault", checked === true); }} />}
          </form>
        )}
        <p className="mt-4 flex flex-wrap items-center gap-x-1 text-xs text-fg-muted" data-testid="other-options">
          {t("billing.methods.otherOptionsBefore")}{" "}
          <Button variant="ghost" size="sm" className="-mx-1 underline underline-offset-2" onClick={onContactSales}>
            {t("billing.methods.otherOptionsAction")}
          </Button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
